import { isApiError } from './ApiError';

export interface ErrorMessageConfig {
  title: string;
  message: string;
  action?: string;
  recoverable?: boolean;
}

const ERROR_MESSAGES = {
  NETWORK_ERROR: {
    title: 'Verkkovirhe',
    message: 'Tarkista internetyhteys ja yritä uudelleen.',
    action: 'Yritä uudelleen',
    recoverable: true,
  },

  TIMEOUT_ERROR: {
    title: 'Pyyntö aikakatkaistiin',
    message: 'Palvelin ei vastannut ajoissa. Yritä uudelleen.',
    action: 'Yritä uudelleen',
    recoverable: true,
  },

  SERVER_ERROR: {
    title: 'Palvelinvirhe',
    message: 'Palvelimessa tapahtui virhe. Yritä hetken kuluttua uudelleen.',
    action: 'Yritä uudelleen',
    recoverable: true,
  },

  UNAUTHORIZED: {
    title: 'Kirjautuminen vaaditaan',
    message: 'Istunto on vanhentunut. Kirjaudu sisään uudelleen.',
    action: 'Kirjaudu sisään',
    recoverable: true,
  },

  FORBIDDEN: {
    title: 'Ei käyttöoikeutta',
    message: 'Sinulla ei ole oikeuksia suorittaa tätä toimintoa.',
    action: 'Takaisin',
    recoverable: false,
  },

  NOT_FOUND: {
    title: 'Tietoja ei löytynyt',
    message: 'Pyytämäsi tiedot eivät ole saatavilla tai ne on poistettu.',
    action: 'Takaisin',
    recoverable: false,
  },

  VALIDATION_ERROR: {
    title: 'Tarkista tiedot',
    message: 'Lomakkeessa on virheitä. Korjaa ne ja yritä uudelleen.',
    action: 'Korjaa',
    recoverable: true,
  },

  CONFLICT_ERROR: {
    title: 'Ristiriita tiedoissa',
    message: 'Tiedot on päivitetty toisen käyttäjän toimesta. Päivitä sivu.',
    action: 'Päivitä sivu',
    recoverable: true,
  },

  // File/upload errors
  FILE_TOO_LARGE: {
    title: 'Tiedosto liian suuri',
    message: 'Valittu tiedosto on liian suuri. Maksimikoko on 10 MB.',
    action: 'Valitse toinen tiedosto',
    recoverable: true,
  },

  RATE_LIMITED: {
    title: 'Liian monta pyyntöä',
    message: 'Liian monta pyyntöä kerralla. Odota hetki ja yritä uudelleen.',
    action: 'Yritä uudelleen',
    recoverable: true,
  },

  UNKNOWN_ERROR: {
    title: 'Odottamaton virhe',
    message: 'Tapahtui odottamaton virhe. Yritä uudelleen tai ota yhteyttä tukeen.',
    action: 'Yritä uudelleen',
    recoverable: true,
  },
} as const;

export type ErrorMessageKey = keyof typeof ERROR_MESSAGES;

/**
 * Get error message from HTTP status code
 */
export function getErrorMessageFromStatus(status: number): ErrorMessageConfig {
  switch (status) {
    case 400:
      return ERROR_MESSAGES.VALIDATION_ERROR;
    case 401:
      return ERROR_MESSAGES.UNAUTHORIZED;
    case 403:
      return ERROR_MESSAGES.FORBIDDEN;
    case 404:
      return ERROR_MESSAGES.NOT_FOUND;
    case 409:
      return ERROR_MESSAGES.CONFLICT_ERROR;
    case 413:
      return ERROR_MESSAGES.FILE_TOO_LARGE;
    case 422:
      return ERROR_MESSAGES.VALIDATION_ERROR;
    case 429:
      return ERROR_MESSAGES.RATE_LIMITED;
    case 500:
    case 502:
    case 503:
    case 504:
      return ERROR_MESSAGES.SERVER_ERROR;
    default:
      return ERROR_MESSAGES.UNKNOWN_ERROR;
  }
}

/**
 * Get error message from Error object
 */
export function getErrorMessageFromError(error: Error | string): ErrorMessageConfig {
  const errorMessage = typeof error === 'string' ? error : error.message;
  const lowerMessage = errorMessage.toLowerCase();

  // Network-related errors
  if (lowerMessage.includes('network') || lowerMessage.includes('fetch')) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  if (lowerMessage.includes('timeout')) {
    return ERROR_MESSAGES.TIMEOUT_ERROR;
  }

  if (lowerMessage.includes('rate limit')) {
    return ERROR_MESSAGES.RATE_LIMITED;
  }

  if (lowerMessage.includes('unauthorized') || lowerMessage.includes('401')) {
    return ERROR_MESSAGES.UNAUTHORIZED;
  }

  if (lowerMessage.includes('forbidden') || lowerMessage.includes('403')) {
    return ERROR_MESSAGES.FORBIDDEN;
  }

  if (lowerMessage.includes('not found') || lowerMessage.includes('404')) {
    return ERROR_MESSAGES.NOT_FOUND;
  }

  return ERROR_MESSAGES.UNKNOWN_ERROR;
}

/** Translation key for transfer-related API errors (condominium transfer/update/revoke). */
export function getTransferErrorTranslationKey(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const lower = message.toLowerCase();
  const status = isApiError(error) ? error.status : undefined;
  if (status === 403 || lower.includes('forbidden') || lower.includes('käyttöoikeus') || lower.includes('puuttuu')) {
    return 'messages.transferForbidden';
  }
  if (
    status === 404 ||
    lower.includes('company not found') ||
    lower.includes('yritystä') ||
    lower.includes('ei löytynyt') ||
    lower.includes('företaget') ||
    lower.includes('hittades inte')
  ) {
    return 'messages.transferCompanyNotFound';
  }
  if (
    lower.includes('not transferred') ||
    lower.includes('ei ole siirretty') ||
    lower.includes('inte överförd') ||
    lower.includes('är inte överförd')
  ) {
    return 'messages.transferCondominiumNotTransferred';
  }
  return 'messages.updateFailed';
}
